import React from 'react';
import { StyleSheet, View } from 'react-native';
import { BodyText, Button, TextField, theme } from '@sweetgreen/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useLocalizationContext } from '@order/Localization';

import type { CreditSectionProps } from '../CreditSectionProps';

// ─────────────────────────────────────────────────────────────────────────────

export const CreditOverviewPromoCodeForm = (
  props: CreditOverviewPromoCodeFormProps,
) => {
  const {
    buttonPlacement = 'right',
    onPressApplyPromoCode,
    hasPromoCodeError,
    isPromoCodeApplied,
    promoCode,
    isSubmittingPromoCode,
    setPromoCode,
    handleShowScanner,
  } = props;

  const shouldSeparatePromoAndGiftCard = useFeatureFlag(
    'CELS-1410-separate-promo-and-gift-card',
  );

  const onPress = React.useCallback(() => {
    onPressApplyPromoCode(promoCode);
  }, [promoCode, onPressApplyPromoCode]);

  const shouldDisableApplyBtn = promoCode.trim() === '';
  const { t } = useLocalizationContext();
  const isBottomButton = buttonPlacement === 'bottom';
  const cameraIcon = isBottomButton ? 'IconCamera' : undefined;
  const inputPadding = isBottomButton ? undefined : styles.inputPadding;

  const buttonA11yLabel = shouldSeparatePromoAndGiftCard
    ? t('account.credit.promo-code-a11y-label')
    : t('account.credit.a11y-label');

  const buttonProps: React.ComponentProps<typeof Button> = {
    testID: 'account.promo-code-button',
    palette: 'primary',
    accessibilityLabel: buttonA11yLabel,
    accessibilityHint: buttonA11yLabel,
    accessibilityRole: 'button',
    accessibilityState: { disabled: shouldDisableApplyBtn },
    disabled: shouldDisableApplyBtn,
    isLoading: isSubmittingPromoCode,
    onPress,
  };

  const promoCodeAlreadyAppliedNoticeText = shouldSeparatePromoAndGiftCard
    ? t('account.notice.credit-promo-code-already-applied')
    : t('account.notice.credit-already-applied');
  const promoCodeAlreadyAppliedNotice = isPromoCodeApplied
    ? promoCodeAlreadyAppliedNoticeText
    : undefined;

  const invalidPromoCodeNoticeText = shouldSeparatePromoAndGiftCard
    ? t('account.notice.credit-promo-code-invalid')
    : t('account.notice.credit-invalid');
  const invalidPromoCodeNotice = hasPromoCodeError
    ? invalidPromoCodeNoticeText
    : undefined;

  const placeholder = shouldSeparatePromoAndGiftCard
    ? t('account.credit.field-promo-code-placeholder')
    : t('account.credit.field-placeholder');

  const helperText = shouldSeparatePromoAndGiftCard
    ? t('account.credit-promo.helper-text-promo-code')
    : t('account.credit-promo.helper-text');

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View>
      <View style={styles.promoCodeWrapper}>
        <View style={[styles.promoCodeInputWrapper, inputPadding]}>
          <TextField
            testID="account.promo-code-field"
            placeholder={placeholder}
            accessibilityLabel={placeholder}
            value={promoCode}
            fieldOptionIcon={cameraIcon}
            fieldOptionOnPress={handleShowScanner}
            onChangeText={setPromoCode}
            onSubmit={onPress}
            notice={promoCodeAlreadyAppliedNotice ?? invalidPromoCodeNotice}
            invalid={hasPromoCodeError}
            noticePalette="caution"
          />
        </View>

        {!isBottomButton && (
          <Button width={125} {...buttonProps}>
            {t('general.apply')}
          </Button>
        )}
      </View>

      <BodyText size={4}>{helperText}</BodyText>

      {isBottomButton ? (
        <Button size="large" style={styles.buttonMargin} {...buttonProps}>
          {t('general.apply')}
        </Button>
      ) : null}
    </View>
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

export type CreditOverviewPromoCodeFormProps = Readonly<{
  buttonPlacement?: 'right' | 'bottom';
  handleShowScanner?: () => void;
}> &
  Pick<
    CreditSectionProps,
    | 'onPressApplyPromoCode'
    | 'hasPromoCodeError'
    | 'isPromoCodeApplied'
    | 'promoCode'
    | 'setPromoCode'
    | 'isSubmittingPromoCode'
  >;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  promoCodeWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: theme.spacing['4'],
  },
  promoCodeInputWrapper: {
    flexGrow: 1,
    marginTop: theme.spacing['6'],
  },
  inputPadding: {
    paddingRight: theme.spacing['8'],
  },
  buttonMargin: {
    marginTop: theme.spacing['10'],
  },
});
